import { Service, type WorkspaceService } from '@toeverything/infra';

import { addTauriAuthHeaders } from '../../cloud/utils/tauri-auth';
import type { SerializedSummaryChunk, SerializedTranscriptionChunk } from './schema';

const AI_API_BASE_URL = 'https://ai.pique.so';
const FLUSH_BASE_DELAY_MS = 2000;
const FLUSH_MAX_DELAY_MS = 60_000;

interface TranscriptionBlobData {
  chunks: SerializedTranscriptionChunk[];
  version: 1;
}

interface SummaryBlobData {
  chunks: SerializedSummaryChunk[];
  version: 1;
}

export class TranscriptionBlobStorage extends Service {
  private readonly transcriptionCache = new Map<string, SerializedTranscriptionChunk[]>();
  private readonly summaryCache = new Map<string, SerializedSummaryChunk[]>();
  private readonly initialized = new Set<string>();
  private readonly loadedFromBlob = new Set<string>();
  private readonly dirty = new Set<string>();
  private readonly pageLoads = new Map<string, Promise<void>>();
  private readonly pendingIndex = new Map<string, { attempts: number; notBefore: number }>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private flushChain: Promise<void> = Promise.resolve();
  private consecutiveFlushFailures = 0;
  private disposed = false;

  constructor(private readonly workspaceService: WorkspaceService) {
    super();
  }

  private get blob() {
    return this.workspaceService.workspace.engine.blob;
  }

  private transcriptionKey(pageId: string): string {
    return `transcription:${pageId}`;
  }

  private summaryKey(pageId: string): string {
    return `summary:${pageId}`;
  }

  async ensurePageLoaded(pageId: string): Promise<void> {
    if (this.initialized.has(pageId)) return;

    const inflight = this.pageLoads.get(pageId);
    if (inflight) return inflight;

    const load = this.loadPage(pageId).finally(() => {
      this.pageLoads.delete(pageId);
    });
    this.pageLoads.set(pageId, load);
    return load;
  }

  private async loadPage(pageId: string): Promise<void> {
    const [transcriptionData, summaryData] = await Promise.all([
      this.loadTranscriptionFromBlob(pageId),
      this.loadSummaryFromBlob(pageId),
    ]);
    this.loadedFromBlob.add(pageId);

    if (this.initialized.has(pageId)) {
      this.mergeLoadedChunks(
        this.transcriptionCache,
        pageId,
        transcriptionData,
        'transcription'
      );
      this.mergeLoadedChunks(this.summaryCache, pageId, summaryData, 'summary');
      return;
    }

    this.transcriptionCache.set(pageId, transcriptionData);
    this.summaryCache.set(pageId, summaryData);
    this.initialized.add(pageId);
  }

  /**
   * Blob saves are whole-file replacements. Never upload from a cache that was
   * populated only by local writes — prime it with the server blob first so a
   * flush can never erase chunks recorded on another device.
   */
  private async primeFromBlobBeforeSave(pageId: string): Promise<void> {
    if (this.loadedFromBlob.has(pageId)) return;

    const [transcriptionData, summaryData] = await Promise.all([
      this.loadTranscriptionFromBlob(pageId),
      this.loadSummaryFromBlob(pageId),
    ]);
    this.loadedFromBlob.add(pageId);

    this.mergeHistoryUnderLive(this.transcriptionCache, pageId, transcriptionData);
    this.mergeHistoryUnderLive(this.summaryCache, pageId, summaryData);
    this.initialized.add(pageId);
  }

  private mergeHistoryUnderLive<T extends { id: string }>(
    cache: Map<string, T[]>,
    pageId: string,
    loaded: T[]
  ): void {
    if (loaded.length === 0) return;
    const live = cache.get(pageId) ?? [];
    const liveIds = new Set(live.map(entry => entry.id));
    const history = loaded.filter(entry => !liveIds.has(entry.id));
    if (history.length === 0) return;
    cache.set(pageId, [...history, ...live]);
  }

  private mergeLoadedChunks<T extends { id: string }>(
    cache: Map<string, T[]>,
    pageId: string,
    loaded: T[],
    type: 'transcription' | 'summary'
  ): void {
    if (loaded.length === 0) return;
    const live = cache.get(pageId) ?? [];
    const liveIds = new Set(live.map(entry => entry.id));
    const history = loaded.filter(entry => !liveIds.has(entry.id));
    if (history.length === 0) return;
    cache.set(pageId, [...history, ...live]);
    this.markDirty(pageId, type);
  }

  getPageTranscriptionSync(pageId: string): SerializedTranscriptionChunk[] {
    return this.transcriptionCache.get(pageId) ?? [];
  }

  setPageTranscription(pageId: string, chunks: SerializedTranscriptionChunk[]): void {
    this.transcriptionCache.set(pageId, [...chunks]);
    this.initialized.add(pageId);
    this.markDirty(pageId, 'transcription');
  }

  addPageTranscriptionChunk(pageId: string, chunk: SerializedTranscriptionChunk): void {
    const chunks = this.transcriptionCache.get(pageId) ?? [];
    chunks.push(chunk);
    this.transcriptionCache.set(pageId, chunks);
    this.initialized.add(pageId);
    this.markDirty(pageId, 'transcription');
  }

  removePageTranscriptionChunk(pageId: string, chunkId: string): boolean {
    const chunks = this.transcriptionCache.get(pageId);
    if (!chunks) return false;

    const index = chunks.findIndex(c => c.id === chunkId);
    if (index === -1) return false;

    chunks.splice(index, 1);
    this.markDirty(pageId, 'transcription');
    return true;
  }

  updatePageTranscriptionChunk(
    pageId: string,
    chunkId: string,
    updates: Partial<SerializedTranscriptionChunk>
  ): boolean {
    const chunks = this.transcriptionCache.get(pageId);
    if (!chunks) return false;

    const chunk = chunks.find(c => c.id === chunkId);
    if (!chunk) return false;

    Object.assign(chunk, updates);
    this.markDirty(pageId, 'transcription');
    return true;
  }

  getPageSummarySync(pageId: string): SerializedSummaryChunk[] {
    return this.summaryCache.get(pageId) ?? [];
  }

  setPageSummary(pageId: string, chunks: SerializedSummaryChunk[]): void {
    this.summaryCache.set(pageId, [...chunks]);
    this.initialized.add(pageId);
    this.markDirty(pageId, 'summary');
  }

  addPageSummaryChunk(pageId: string, chunk: SerializedSummaryChunk): void {
    const chunks = this.summaryCache.get(pageId) ?? [];
    chunks.push(chunk);
    this.summaryCache.set(pageId, chunks);
    this.markDirty(pageId, 'summary');
  }

  removePageSummaryChunk(pageId: string, chunkId: string): boolean {
    const chunks = this.summaryCache.get(pageId);
    if (!chunks) return false;

    const index = chunks.findIndex(c => c.id === chunkId);
    if (index === -1) return false;

    chunks.splice(index, 1);
    this.markDirty(pageId, 'summary');
    return true;
  }

  updatePageSummaryChunk(
    pageId: string,
    chunkId: string,
    updates: Partial<SerializedSummaryChunk>
  ): boolean {
    const chunks = this.summaryCache.get(pageId);
    if (!chunks) return false;

    const chunk = chunks.find(c => c.id === chunkId);
    if (!chunk) return false;

    Object.assign(chunk, updates);
    this.markDirty(pageId, 'summary');
    return true;
  }

  private markDirty(pageId: string, type: 'transcription' | 'summary'): void {
    this.dirty.add(`${type}:${pageId}`);
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.disposed || this.flushTimer) return;
    const delay = Math.min(
      FLUSH_BASE_DELAY_MS * 2 ** this.consecutiveFlushFailures,
      FLUSH_MAX_DELAY_MS
    );
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flushDirtyPages();
    }, delay);
  }

  async flushNow(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    await this.flushDirtyPages();
  }

  private flushDirtyPages(): Promise<void> {
    const run = this.flushChain.then(() => this.flushDirtyPagesExclusive());
    this.flushChain = run;
    return run;
  }

  private async flushDirtyPagesExclusive(): Promise<void> {
    const toFlush = [...this.dirty];
    this.dirty.clear();
    let failed = false;

    for (const key of toFlush) {
      const [type, pageId] = key.split(':') as ['transcription' | 'summary', string];
      try {
        if (type === 'transcription') {
          await this.saveTranscriptionToBlob(pageId);
          this.pendingIndex.set(pageId, { attempts: 0, notBefore: 0 });
        } else {
          await this.saveSummaryToBlob(pageId);
        }
      } catch {
        failed = true;
        this.dirty.add(key);
      }
    }
    this.consecutiveFlushFailures = failed
      ? this.consecutiveFlushFailures + 1
      : 0;

    await this.flushPendingIndexRequests();

    if (this.dirty.size > 0 || this.pendingIndex.size > 0) this.scheduleFlush();
  }

  private async flushPendingIndexRequests(): Promise<void> {
    const now = Date.now();
    for (const [pageId, state] of [...this.pendingIndex]) {
      if (state.notBefore > now) continue;
      try {
        await this.requestTranscriptIndex(pageId);
        this.pendingIndex.delete(pageId);
      } catch (error) {
        const attempts = state.attempts + 1;
        this.pendingIndex.set(pageId, {
          attempts,
          notBefore: now + Math.min(2000 * 2 ** attempts, 5 * 60 * 1000),
        });
        console.error(
          `[transcription] transcript index request failed for ${pageId} (attempt ${attempts})`,
          error
        );
      }
    }
  }

  private async loadTranscriptionFromBlob(pageId: string): Promise<SerializedTranscriptionChunk[]> {
    try {
      const blob = await this.blob.get(this.transcriptionKey(pageId));
      if (!blob) return [];
      const text = await blob.text();
      const data: TranscriptionBlobData = JSON.parse(text);
      return data.chunks ?? [];
    } catch {
      return [];
    }
  }

  private async saveTranscriptionToBlob(pageId: string): Promise<void> {
    await this.primeFromBlobBeforeSave(pageId);
    const chunks = this.transcriptionCache.get(pageId) ?? [];
    const data: TranscriptionBlobData = { chunks, version: 1 };
    const json = JSON.stringify(data);
    const blob = new Blob([json], { type: 'application/json' });
    await this.blob.set(this.transcriptionKey(pageId), blob);
  }

  private async requestTranscriptIndex(pageId: string): Promise<void> {
    const headers = await addTauriAuthHeaders({});
    const res = await fetch(
      `${AI_API_BASE_URL}/api/embed/transcript/${encodeURIComponent(pageId)}`,
      {
        method: 'POST',
        headers,
        credentials: 'include',
      }
    );
    if (!res.ok) throw new Error(`Transcript index failed: ${res.status}`);
  }

  private async loadSummaryFromBlob(pageId: string): Promise<SerializedSummaryChunk[]> {
    try {
      const blob = await this.blob.get(this.summaryKey(pageId));
      if (!blob) return [];
      const text = await blob.text();
      const data: SummaryBlobData = JSON.parse(text);
      return data.chunks ?? [];
    } catch {
      return [];
    }
  }

  private async saveSummaryToBlob(pageId: string): Promise<void> {
    await this.primeFromBlobBeforeSave(pageId);
    const chunks = this.summaryCache.get(pageId) ?? [];
    const data: SummaryBlobData = { chunks, version: 1 };
    const json = JSON.stringify(data);
    const blob = new Blob([json], { type: 'application/json' });
    await this.blob.set(this.summaryKey(pageId), blob);
  }

  dispose(): void {
    this.disposed = true;
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    void this.flushDirtyPages();
  }
}
